import '../collection-action-menu/collection-action-menu.js';
import {css, CSSResult, html, LitElement, nothing, unsafeCSS} from 'lit';
import {customElement, property} from 'lit/decorators.js';
import {CollectionTrackingContext, Messages, WishlistCollection} from '../../../@types/wishlist.js';
import {consume} from '@lit/context';
import typographyStyles from '../../../styles/responsive-nui-typography.scss';
import styles from './wishlist-collection-head.scss';
import eyeClosed from '@breuni-ui/bdk/dist/design-tokens/images/icons/o-ic-24_eye-closed.svg';
import eye from '@breuni-ui/bdk/dist/design-tokens/images/icons/o-ic-24_eye.svg';
import concierge from '@breuni-ui/bdk/dist/design-tokens/images/icons/o-ic-24_concierge.svg';
import sharedIcon from '@breuni-ui/bdk/dist/design-tokens/images/icons/o-ic-24_send.svg';
import {collectionMenuContext} from '../../../scripts/context/menuContext.js';
import {CollectionMenuState} from '../../../scripts/context/menu-state.js';
import {classMap} from 'lit/directives/class-map.js';
import {i18nContext} from '../../../scripts/context/i18nContext.js';
import {addTitleToIcon, hideIconFromAssistiveTechnology} from '../icon/icon-accessibility.js';
import {collectionsContext} from '../../../scripts/context/wishlistContext.js';
import {CollectionsState} from '../../../scripts/context/wishlist-state.js';

@customElement('ents-wishlist-collection-head')
export class WishlistCollectionHead extends LitElement {
  @consume({context: i18nContext})
  protected messages!: Messages;

  @property({type: Boolean, attribute: 'with-headline', reflect: true})
  private withHeadline: boolean = false;

  @property({type: Boolean, attribute: 'curated-collection', reflect: true})
  private isCuratedCollection: boolean = false;

  @property({type: Object, attribute: 'collection-tracking-context'})
  private collectionTrackingContext!: CollectionTrackingContext;

  @property({type: Array, attribute: 'wishlist-collection'})
  private wishlistCollection!: WishlistCollection;

  @consume({context: collectionMenuContext, subscribe: true})
  @property({attribute: false})
  private collectionMenuState!: CollectionMenuState;

  @consume({context: collectionsContext, subscribe: true})
  @property({attribute: false})
  private collectionsState!: CollectionsState;

  static get styles(): CSSResult[] {
    return [css`${unsafeCSS(styles)}`, css`${unsafeCSS(typographyStyles)}`];
  }

  private openActionMenuLayer(col: WishlistCollection) {
    this.collectionMenuState.openActionMenu(col, this.collectionTrackingContext);
  }

  private openShareMenuLayer(col: WishlistCollection) {
    this.collectionMenuState.openShareView(col, this.collectionTrackingContext);
  }

  private getShareStatusIcon() {
    if (this.wishlistCollection.sharedWithTopCustomers) {
      return addTitleToIcon(concierge(), this.messages['ents_wishlist_collection_share_state_shared']);
    }
    if (this.wishlistCollection.isSharingAllowed) {
      return addTitleToIcon(eye(), this.messages['ents_wishlist_collection_share_state_shared']);
    }
    return addTitleToIcon(eyeClosed(), this.messages['ents_wishlist_collection_share_state_private']);
  }

  connectedCallback() {
    super.connectedCallback();

    this.dispatchEvent(new Event('scroll-top'));
  }

  render() {
    const isShared = this.collectionsState.entities.some(col => col.isSharingAllowed) || this.wishlistCollection.sharedWithTopCustomers;
    const showShareStatusIcon = !this.withHeadline && !this.isCuratedCollection && isShared;
    const shareStatusIcon = this.getShareStatusIcon();

    return html`
      <div
        class="ents-wishlist-collection-head ${classMap({
          'ents-wishlist-collection-head--with-headline': this.withHeadline,
          'ents-wishlist-collection-head--curated-collection': this.isCuratedCollection
        })}">
        <span
          class="ents-wishlist-collection-head__name ents-copy-bold fs-mask"
          data-test-ents-wishlist-collection-name
        >
          ${showShareStatusIcon
            ? html`<span class="ents-wishlist-collection-head__name--icon">${shareStatusIcon}</span>`
            : nothing
          }
          <span class="ents-wishlist-collection-head__name--text">
            ${this.wishlistCollection.name}
          </span>
        </span>
        ${this.withHeadline && !this.isCuratedCollection ? html`
          <button
            class="ents-wishlist-collection-head__share--button"
            type="button"
            aria-label="${this.messages['ents_wishlist_collection_actionmenu_share']}"
            @click="${(event: Event) => {
              event.preventDefault();
              this.openShareMenuLayer(this.wishlistCollection);
            }}"
            data-test-ents-wishlist-collection-head-share-button
          >
            ${hideIconFromAssistiveTechnology(sharedIcon())}
          </button>
        ` : nothing}
        ${!this.isCuratedCollection ? html`
          <button
            class="ents-wishlist-collection-head__action-menu--button"
            type="button"
            aria-label="${this.messages['ents_wishlist_collection_actionmenu_button_description']}"
            @click="${(event: Event) => {
              event.preventDefault();
              this.openActionMenuLayer(this.wishlistCollection);
            }}"
            data-test-ents-wishlist-collection-action-button
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" aria-hidden="true">
              <circle cx="4.667" cy="12" r="2" fill="currentColor"/>
              <circle cx="12.667" cy="12" r="2" fill="currentColor"/>
              <circle cx="20.667" cy="12" r="2" fill="currentColor"/>
            </svg>
          </button>
        ` : nothing}
        ${this.withHeadline && this.isCuratedCollection ? html`
          <wishlist-alert
            cookie-id="ents-wishlist-curated-list-hint"
            class="ents-wishlist-collection-head__hint"
          >
            <p class="ents-copy-small-bold">
              ${this.messages['ents_wishlist_curated_collection_hint']}<br/>
              <a href="#" @click="${() => console.error('not implemented yet')}"
                 class="bdk-link-primary" size="small">
                ${this.messages['ents_wishlist_curated_collection_hint_linktext']}
              </a>
            </p>
          </wishlist-alert>
        ` : nothing}
      </div>
    `;
  }
}
